import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";
import { Copy, Check, Link2, Ban, Loader2 } from "lucide-react";

export function ShareLinkDialog({ title, description, apiPath, publicPath, open, onClose, testPrefix = "share" }) {
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !apiPath) return;
    setLoading(true);
    api.post(apiPath)
      .then(({ data }) => setLink(data))
      .catch((err) => toast.error(err?.response?.data?.detail || "Couldn't create share link"))
      .finally(() => setLoading(false));
  }, [open, apiPath]);

  const url = link?.token ? `${window.location.origin}${publicPath}/${link.token}` : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const el = document.createElement("textarea");
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopied(true);
    toast.success("Link copied to clipboard");
    setTimeout(() => setCopied(false), 1600);
  };

  const revoke = async () => {
    await api.delete(apiPath);
    setLink(null);
    toast.success("Share link revoked");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid={`${testPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Link2 className="h-4 w-4" /> {title}
          </DialogTitle>
          <DialogDescription className="text-xs">{description}</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        ) : link?.token ? (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input readOnly value={url} className="h-9 font-mono text-xs" data-testid={`${testPrefix}-link-input`} />
              <Button size="icon" className="h-9 w-9 shrink-0" onClick={copy} data-testid={`${testPrefix}-copy-button`}>
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <Button variant="ghost" size="sm" className="h-8 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={revoke} data-testid={`${testPrefix}-revoke-button`}>
              <Ban className="mr-1.5 h-3.5 w-3.5" /> Revoke link
            </Button>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export default function AccountShareDialog({ accountId, accountName, open, onClose }) {
  return (
    <ShareLinkDialog
      open={open} onClose={onClose} testPrefix="account-share"
      title={`Share \u201c${accountName}\u201d`}
      description="Anyone with this link gets a live, read-only view of this account's full analytics, including deposits and withdrawals."
      apiPath={accountId ? `/accounts/${accountId}/share` : null}
      publicPath="/shared/account"
    />
  );
}

export function PortfolioShareDialog({ portfolioId, portfolioName, open, onClose }) {
  return (
    <ShareLinkDialog
      open={open} onClose={onClose} testPrefix="portfolio-share"
      title={`Share portfolio \u201c${portfolioName}\u201d`}
      description="Anyone with this link gets a live, read-only view of the whole portfolio: combined numbers plus the full analytics of every account in it. Revoking the link closes all of them at once."
      apiPath={portfolioId ? `/portfolios/${portfolioId}/share` : null}
      publicPath="/shared/portfolio"
    />
  );
}
